import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import FaqSection from './components/FaqSection';
import WhatsAppEnquiryForm from './components/WhatsAppEnquiryForm';

// --- Data extracted and adapted from the reference link ---
const coursePackages = [
    {
        category: "Quality Assurance",
        title: "Automation Testing with Selenium & C#",
        description: "Master QA automation. Learn C# for Selenium WebDriver, BDD with SpecFlow, and build a CI/CD pipeline for the UK market.",
    },
    {
        category: "Backend & Web API",
        title: "Full-Stack .NET Developer Course",
        description: "Become a job-ready .NET Developer. This course teaches C#, Entity Framework, and how to build and deploy secure RESTful APIs.",
    },
    {
        category: "DevOps & Cloud",
        title: "DevOps Engineering Bootcamp",
        description: "Launch your DevOps career. Master CI/CD with GitHub Actions, containerize with Docker, and orchestrate with Kubernetes (K8s).",
    },
    {
        category: "Database Management",
        title: "SQL Server & T-SQL for Developers",
        description: "Learn essential SQL skills for developer and data analyst roles, from basic queries to advanced T-SQL and performance tuning.",
    },
    {
        category: "Cloud Computing",
        title: "AWS Cloud Engineering Bootcamp",
        description: "Prepare for AWS certification and cloud engineering roles. Covers core AWS services like EC2, S3, Lambda, and DynamoDB.",
    },
    {
        category: "Artificial Intelligence",
        title: "Generative AI & LLM Course",
        description: "Build practical AI applications. Master prompt engineering, build RAG pipelines, and integrate large language models (LLMs).",
    }
];

function ITCourses() {
    const [prefill, setPrefill] = useState(null);

    const enquireAbout = (courseTitle) => {
        setPrefill({ course: courseTitle });
        document.getElementById("enquiry")?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <>
            <Helmet>
                <title>UK IT Training Courses for Indians | DevOps, AWS, QA (2026)</title>
                <meta 
                    name="description" 
                    content="Job-ready IT training for Indians in the UK: automation testing, DevOps, AWS, .NET and Generative AI courses with mentor support. Free career consultation."
                />
                {/* Social previews (WhatsApp, Facebook, X) — otherwise the homepage values are used */}
                <meta property="og:title" content="UK IT Training Courses for Indians | DevOps, AWS, QA (2026)" />
                <meta property="og:description" content="Job-ready IT training for Indians in the UK: automation testing, DevOps, AWS, .NET and Generative AI courses with mentor support. Free career consultation." />
                <meta name="twitter:title" content="UK IT Training Courses for Indians | DevOps, AWS, QA (2026)" />
                <meta name="twitter:description" content="Job-ready IT training for Indians in the UK: automation testing, DevOps, AWS, .NET and Generative AI courses with mentor support. Free career consultation." />
            </Helmet>

            <div className="container mx-auto p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md my-6">
                <div className="max-w-5xl mx-auto text-left">
                    <h1 className="text-3xl md:text-4xl font-extrabold text-indigo-700 dark:text-indigo-300 mb-4 text-center">
                        Job-Ready IT Training for Indians in the UK
                    </h1>
                    <p className="text-lg text-gray-700 dark:text-gray-300 mb-8 text-center">
                        Launch or advance your tech career in the UK. We connect you with expert-led online courses, internships, and placement programs designed to give you the skills UK employers are looking for.
                    </p>

                    <WhatsAppEnquiryForm
                        id="enquiry"
                        title="Free IT Career Consultation"
                        intro="Confused about which course to choose? Tell us about yourself for a free, no-obligation consultation with a career expert."
                        greeting="Hi! I'm interested in IT training."
                        submitLabel="Get Free Consultation"
                        prefill={prefill}
                        fields={[
                            { name: "name", label: "Your name", required: true },
                            { name: "course", label: "Course", type: "select", required: true, options: [...coursePackages.map((c) => c.title), "Not sure - need advice", "Other"] },
                            { name: "background", label: "Background", type: "select", required: true, options: ["IT professional", "Non-IT, switching to IT", "Student / fresher", "Career break"] },
                            { name: "experience", label: "IT experience", type: "select", options: ["None", "Under 2 years", "2-5 years", "5+ years"] },
                            { name: "visa", label: "Visa status", type: "select", options: ["Student", "Graduate Route", "Skilled Worker", "Dependant", "ILR / British citizen", "Need sponsorship (outside UK)", "Other"] },
                            { name: "start", label: "When do you want to start?", type: "select", options: ["As soon as possible", "Within 1 month", "In 1-3 months", "Just exploring"] },
                            { name: "notes", label: "Anything else?", type: "textarea", requiredIfOther: true, placeholder: "Current skills, career goal..." },
                        ]}
                    />
                    
                    <div>
                        <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-6 text-center">In-Demand Courses & Career Programs</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {coursePackages.map((course, index) => (
                                <div key={index} className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg hover:shadow-2xl dark:hover:shadow-blue-500/20 transition-shadow duration-300 overflow-hidden flex flex-col">
                                    <div className="p-6 flex-grow">
                                        <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">{course.category}</span>
                                        <h3 className="text-xl font-bold text-gray-800 dark:text-slate-100 mt-1">{course.title}</h3>
                                        <p className="text-gray-600 dark:text-slate-300 mt-4 text-sm">{course.description}</p>
                                    </div>
                                    <div className="bg-gray-50 dark:bg-slate-700/50 p-4 mt-auto">
                                        <p className="text-sm font-semibold text-gray-800 dark:text-slate-100">Self-Paced with Mentor Support</p>
                                        <button
                                            type="button"
                                            onClick={() => enquireAbout(course.title)}
                                            className="mt-3 px-4 py-2 rounded-full bg-green-500 hover:bg-green-600 text-white text-sm font-semibold transition duration-200"
                                        >
                                            Enquire about this course
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="mt-12 space-y-8">
                        <div>
                            <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-3">How to Choose the Right IT Course</h2>
                            <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400 pl-4">
                                <li><strong>Start from the job you want:</strong> read ten UK job adverts for that role and note the skills that keep appearing. Choose a course that covers them.</li>
                                <li><strong>Look for hands-on projects:</strong> the best courses have you build and deploy real things, not just watch videos.</li>
                                <li><strong>Check who teaches it:</strong> trainers with recent industry experience teach what employers actually use.</li>
                                <li><strong>Ask what job support means:</strong> CV reviews, mock interviews and introductions are useful. Be wary of anyone who guarantees a job or visa sponsorship.</li>
                                <li><strong>Be realistic about time:</strong> if you are working or studying, a self-paced course with mentor support may suit you better than a full-time bootcamp.</li>
                            </ul>
                        </div>

                        <div>
                            <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-3">Certifications UK Employers Recognise</h2>
                            <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400 pl-4">
                                <li><strong>Cloud:</strong> AWS Certified Cloud Practitioner and AWS Solutions Architect – Associate; Microsoft Azure Fundamentals (AZ-900) and Azure Administrator (AZ-104).</li>
                                <li><strong>Testing:</strong> ISTQB Certified Tester Foundation Level is widely listed in UK QA job adverts.</li>
                                <li><strong>DevOps:</strong> Certified Kubernetes Administrator (CKA) for container and platform roles.</li>
                            </ul>
                            <p className="text-gray-600 dark:text-gray-400 mt-3">Certifications help your CV get past keyword filters, but interviewers will test what you can actually do. Pair each certification with a project you can talk about.</p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-3">Build a Portfolio That Gets Interviews</h2>
                            <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400 pl-4">
                                <li>Put two or three solid projects on GitHub with a clear README explaining what they do and how to run them.</li>
                                <li>Show the full cycle: tests, a CI/CD pipeline and a deployment to a cloud platform.</li>
                                <li>Link your GitHub from your CV and LinkedIn, and keep LinkedIn up to date — UK recruiters use it heavily.</li>
                            </ul>
                        </div>

                        <div>
                            <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-3">What UK Employers Look For</h2>
                            <p className="text-gray-600 dark:text-gray-400">Beyond technical skills, UK tech teams value clear written and spoken communication, experience of Agile or Scrum ways of working, and the ability to explain your decisions. A UK-format CV makes a big difference — see our <a href="/cv-and-interview-help" className="text-indigo-600 dark:text-indigo-400 hover:underline">CV and interview guide</a> for how to adapt an Indian resume.</p>
                        </div>
                    </div>

                    <FaqSection faqs={[
                        { q: "Which IT courses are in demand in the UK for 2026?", a: "For 2026, the highest demand in the UK is for DevOps, Cloud Computing (especially AWS and Azure), Data Analytics, and Software Testing Automation. These fields often have roles eligible for Skilled Worker visa sponsorship." },
                        { q: "Do you provide internship and placement support for Indian students?", a: "Yes, we partner with training providers who offer real-world internship programs and dedicated placement support. This includes CV optimization, interview preparation, and job referrals to help you transition from training to a full-time IT role in the UK." },
                        { q: "Can I get an IT job in the UK without UK experience?", a: "Yes. While challenging, it's possible. The best way to bridge the gap is with a UK-focused training program and a real-world internship. This demonstrates to employers that you have relevant, practical skills and understand the UK work environment." },
                    ]} />
                </div>
            </div>
        </>
    );
}

export default ITCourses;